import { type AsyncResult, SynchrounousState, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { generateGenericCacheId } from "@/cache/GeoJSONCache/utils";
import { networkCaches } from "@/cache/GlobalCache";
import { earthquakeLayerConfig } from "@/constants/layerConfigAttributes";
import type { CheckedProps, LayerBase, PropsChecker } from "@/layers";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import { MapboxGeoJSONLayer } from "@/layers/geojson/MapboxGeoJSONLayer";
import { parseHexWithAlpha } from "@/layers/utility/color";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { formatDepthKm, formatMagnitude } from "@/layers/utility/round";
import { thresholdColorExpression } from "@/layers/utility/thresholdColorExpression";
import { type ThresholdColor, transformColorThresholds } from "@/overlay/components/CommonParts/Threshold/types";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { ConditionOptions } from "@/utility/conditionalExpressions";
import { safeFmt } from "@/utility/safeFmt";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import { Area, CoordinateSystem, type GeoJSONFeatureCollection } from "@mm/api.meteomatics.com";
import type { GenericGeoJsonRequest } from "@mm/api.meteomatics.com";
import type { EarthquakeLayer, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { type DateTime, Duration } from "luxon";
import mapboxgl, { type GeoJSONSourceSpecification, type SymbolLayout } from "mapbox-gl";
import { mapSourceLabel } from "../../utility/earthquakeSourceLabel";
import { renderPopup } from "../../utility/popup";
import {
  POINT_ICON_MIN_SIZED_MAGNITUDE,
  POINT_ICON_PIXEL_RATIO,
  POINT_ICON_SIZE_AT_MAGNITUDE_ONE,
  POINT_ICON_SIZE_AT_MAGNITUDE_TEN,
  borderWidthFromOuterRadius,
  buildCompositePointIconImage,
  clusterRadiusFromMagnitude,
  colorHash,
  pieSlicePath,
  toSemiTransparentFillColor,
} from "./utils/iconUtils";

/**
 * Earthquake layer:
 * - fetches GeoJSON from Official Alerts "get_earthquakes"
 * - supports threshold-based point coloring
 * - supports clustered mode with pie-chart cluster markers when thresholds are configured
 */
export class EarthquakeLayerImpl extends MapboxGeoJSONLayer<EarthquakeLayer> {
  readonly mapboxCircleLayerId: string;
  readonly mapboxIconLayerId: string;
  readonly mapboxClusterCircleLayerId: string;
  readonly mapboxClusterCountLayerId: string;
  readonly mapboxClusteredPointCircleLayerId: string;
  readonly mapboxClusteredPointIconLayerId: string;

  private readonly clusteredSourceId: string;
  private readonly popup = new mapboxgl.Popup({ maxWidth: "90vw", className: "mapbox-popover mapbox-popover-nwa" });
  private readonly clusterPieMarkers = new Map<number, mapboxgl.Marker>();
  private readonly pointCompositeIconIds = new Set<string>();
  private readonly displayTimeChanged = new ChangeDetector<number>();

  private readonly onMouseEnter = () => {
    this.scene.getMapboxMap().getCanvas().style.cursor = "pointer";
  };

  private readonly onMouseLeave = () => {
    this.scene.getMapboxMap().getCanvas().style.cursor = "";
  };

  private readonly onPointLayerClick = (e: mapboxgl.MapLayerMouseEvent) => {
    if (!e.features || !e.features.length) {
      return;
    }
    const feature = e.features[0];
    if (feature.geometry.type !== "Point") {
      return;
    }

    const coords = (feature.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
    while (Math.abs(e.lngLat.lng - coords[0]) > 180) {
      coords[0] += e.lngLat.lng > coords[0] ? 360 : -360;
    }

    const html = this.renderEarthquakePopup(feature.properties || {});
    this.popup.setLngLat([coords[0], coords[1]]).setHTML(html).addTo(this.scene.getMapboxMap());
  };

  private readonly onClusterLayerClick = (e: mapboxgl.MapLayerMouseEvent) => {
    if (!e.features || !e.features.length) {
      return;
    }
    const feature = e.features[0] as mapboxgl.MapboxGeoJSONFeature;
    const clusterId = Number(feature.properties?.cluster_id);

    if (!Number.isFinite(clusterId)) {
      return;
    }

    const coords =
      feature.geometry.type === "Point"
        ? (feature.geometry.coordinates as [number, number])
        : ([e.lngLat.lng, e.lngLat.lat] as [number, number]);

    this.expandCluster(clusterId, coords);
  };

  private readonly onSourceData = (e: any) => {
    if (e?.sourceId === this.clusteredSourceId) {
      this.syncClusterPieMarkers();
    }
  };

  private readonly onViewportChanged = () => {
    this.syncClusterPieMarkers();
  };

  private readonly normalizeEarthquakeFeatureCollection = (
    collection: GeoJSONFeatureCollection,
  ): GeoJSONFeatureCollection => {
    const features = (collection.features || []).map((feature: any) => {
      const properties = feature?.properties || {};
      const magnitude = Number.parseFloat(String(properties.magnitude));

      return {
        ...feature,
        properties: {
          ...properties,
          value: Number.isFinite(magnitude) ? magnitude : null,
        },
      };
    });

    return {
      ...collection,
      features,
    } as GeoJSONFeatureCollection;
  };

  constructor(id: number, props: EarthquakeLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone, "symbol");

    this.mapboxCircleLayerId = `${this.humanReadableId()}-circle`;
    this.mapboxIconLayerId = `${this.humanReadableId()}-icon`;
    this.mapboxClusteredPointCircleLayerId = `${this.humanReadableId()}-clustered-point-circle`;
    this.mapboxClusteredPointIconLayerId = `${this.humanReadableId()}-clustered-point-icon`;
    this.mapboxClusterCircleLayerId = `${this.humanReadableId()}-cluster-circle`;
    this.mapboxClusterCountLayerId = `${this.humanReadableId()}-cluster-count`;
    this.clusteredSourceId = `${this.humanReadableId()}-cluster-src`;

    this.addRawSourceLayers();
    this.addClusteredSource();
    this.addClusteredSourceLayers();
    this.setEventListeners();
    this.applyLayerVisibility(this.props.show);
  }

  humanReadableId(): string {
    return safeFmt`metx.earthquakes#${this.uid}`;
  }

  private getDefaultColor(): string {
    return this.props.element_color || earthquakeLayerConfig.defaultElementColor;
  }

  private getThresholds(): ThresholdColor[] {
    return transformColorThresholds(this.props.thresholds);
  }

  private hasThresholds(): boolean {
    return this.getThresholds().length > 0;
  }

  private isClusteringOn(): boolean {
    return this.props.custom_options?.clustering ?? earthquakeLayerConfig.defaultEarthquakeLayerClustering;
  }

  private shouldRenderPieClusters(): boolean {
    return this.isClusteringOn() && this.props.show && this.hasThresholds();
  }

  private getScale(): number {
    return this.props.custom_options?.icon_size ?? 1;
  }

  private getPointLayerIds(): string[] {
    return [this.mapboxIconLayerId, this.mapboxClusteredPointIconLayerId];
  }

  private getClusterLayerIds(): string[] {
    return [this.mapboxClusterCircleLayerId, this.mapboxClusterCountLayerId];
  }

  private getHoverLayerIds(): string[] {
    return [...this.getPointLayerIds(), ...this.getClusterLayerIds()];
  }

  private magnitudeCircleRadiusExpression(scale: number): mapboxgl.Expression {
    return ["*", ["interpolate", ["linear"], ["to-number", ["get", "magnitude"]], 0, 4, 10, 40], scale];
  }

  private magnitudeIconSizeExpression(scale: number): mapboxgl.Expression {
    return [
      "*",
      [
        "interpolate",
        ["linear"],
        ["to-number", ["get", "magnitude"]],
        POINT_ICON_MIN_SIZED_MAGNITUDE,
        POINT_ICON_SIZE_AT_MAGNITUDE_ONE,
        10,
        POINT_ICON_SIZE_AT_MAGNITUDE_TEN,
      ],
      scale,
    ];
  }

  private clusterCircleRadiusExpression(scale: number): mapboxgl.Expression {
    return ["*", ["step", ["to-number", ["get", "point_count"]], 16, 10, 20, 50, 26, 100, 32], scale];
  }

  private clusterTextSizeExpression(scale: number): mapboxgl.Expression {
    return ["*", ["step", ["to-number", ["get", "point_count"]], 11, 10, 12, 50, 13, 100, 14], scale];
  }

  private pointColorExpression(): mapboxgl.Expression | string {
    return thresholdColorExpression(this.getDefaultColor(), this.getThresholds());
  }

  private getCompositePointIconId(color: string): string {
    return `${this.humanReadableId()}-composite-point-${colorHash(color)}`;
  }

  private ensureCompositePointIcon(color: string): string {
    const fallbackIcon = "mm-earthquake";
    const map = this.scene.getMapboxMap() as any;
    if (typeof map?.hasImage !== "function" || typeof map?.addImage !== "function") {
      return fallbackIcon;
    }

    const normalizedColor = parseHexWithAlpha(color);
    const iconId = this.getCompositePointIconId(normalizedColor);

    try {
      if (map.hasImage(iconId)) {
        return iconId;
      }
    } catch (_error) {
      return fallbackIcon;
    }

    const image = buildCompositePointIconImage(normalizedColor);
    if (!image) {
      return fallbackIcon;
    }

    try {
      map.addImage(iconId, image, { pixelRatio: POINT_ICON_PIXEL_RATIO });
      this.pointCompositeIconIds.add(iconId);
      return iconId;
    } catch (_error) {
      return fallbackIcon;
    }
  }

  private pointIconExpression(): mapboxgl.Expression | string {
    const defaultIcon = this.ensureCompositePointIcon(this.getDefaultColor());
    const thresholds = this.getThresholds();

    if (!thresholds.length) {
      return defaultIcon;
    }

    const caseValue: mapboxgl.Expression = ["case", ["==", ["get", "value"], null], defaultIcon];
    for (const threshold of thresholds) {
      caseValue.push([threshold.condition, ["get", "value"], threshold.value]);
      caseValue.push(this.ensureCompositePointIcon(threshold.color));
    }
    caseValue.push(defaultIcon);
    return caseValue;
  }

  private updatePointColorPaint(): void {
    const pointColor = this.pointColorExpression();
    const pointIcon = this.pointIconExpression();
    const map = this.scene.getMapboxMap();
    map.setPaintProperty(this.mapboxCircleLayerId, "circle-color", pointColor);
    map.setPaintProperty(this.mapboxClusteredPointCircleLayerId, "circle-color", pointColor);
    map.setLayoutProperty(this.mapboxIconLayerId, "icon-image", pointIcon);
    map.setLayoutProperty(this.mapboxClusteredPointIconLayerId, "icon-image", pointIcon);
  }

  private addRawSourceLayers() {
    const map = this.scene.getMapboxMap();

    map.addLayer({
      id: this.mapboxCircleLayerId,
      type: "circle",
      source: this.getMapboxSource(),
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": this.magnitudeCircleRadiusExpression(this.getScale()),
        "circle-color": this.pointColorExpression(),
        "circle-opacity": this.props.opacity ?? 1,
        "circle-stroke-color": "#FFFFFF",
        "circle-stroke-width": 2,
      },
      layout: { visibility: "none" },
    });

    map.addLayer({
      id: this.mapboxIconLayerId,
      type: "symbol",
      source: this.getMapboxSource(),
      filter: ["!", ["has", "point_count"]],
      paint: {
        "icon-opacity": this.props.opacity ?? 1,
      },
      layout: {
        visibility: "none",
        "icon-image": this.pointIconExpression(),
        "icon-size": this.magnitudeIconSizeExpression(this.getScale()),
        "icon-allow-overlap": true,
      },
    });
  }

  private thresholdClusterPropertyName(index: number): string {
    return `threshold_count_${index}`;
  }

  private defaultClusterPropertyName(): string {
    return "threshold_count_default";
  }

  private clusterMagnitudeSumPropertyName(): string {
    return "cluster_magnitude_sum";
  }

  private toConditionExpression(condition: string, left: mapboxgl.Expression, right: number): mapboxgl.Expression {
    switch (condition) {
      case ConditionOptions["<"]:
        return ["<", left, right] as mapboxgl.Expression;
      case ConditionOptions["<="]:
        return ["<=", left, right] as mapboxgl.Expression;
      case ConditionOptions[">"]:
        return [">", left, right] as mapboxgl.Expression;
      case ConditionOptions[">="]:
        return [">=", left, right] as mapboxgl.Expression;
      default:
        return ["==", left, right] as mapboxgl.Expression;
    }
  }

  private createClusterProperties(): Record<string, unknown> {
    const thresholds = this.getThresholds();
    if (!thresholds.length) {
      return {};
    }

    const clusterProperties: Record<string, unknown> = {};
    const valueExpression = ["get", "value"] as mapboxgl.Expression;
    const previousConditions: mapboxgl.Expression[] = [];

    thresholds.forEach((threshold, index) => {
      const currentCondition = this.toConditionExpression(threshold.condition, valueExpression, threshold.value);
      const previousNonMatches = previousConditions.map((expr) => ["!", expr] as mapboxgl.Expression);
      const firstMatchExpression = previousNonMatches.length
        ? (["all", currentCondition, ...previousNonMatches] as mapboxgl.Expression)
        : currentCondition;

      clusterProperties[this.thresholdClusterPropertyName(index)] = ["+", ["case", firstMatchExpression, 1, 0]];
      previousConditions.push(currentCondition);
    });

    const unmatchedExpression = previousConditions.length
      ? (["all", ...previousConditions.map((expr) => ["!", expr] as mapboxgl.Expression)] as mapboxgl.Expression)
      : (["literal", true] as mapboxgl.Expression);

    clusterProperties[this.defaultClusterPropertyName()] = ["+", ["case", unmatchedExpression, 1, 0]];
    clusterProperties[this.clusterMagnitudeSumPropertyName()] = ["+", ["to-number", ["get", "magnitude"], 0]];

    return clusterProperties;
  }

  private clusteredSourceSpec(): GeoJSONSourceSpecification {
    const spec: GeoJSONSourceSpecification = {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
      cluster: true,
      clusterRadius: 30,
      clusterMaxZoom: 10,
    };

    const clusterProperties = this.createClusterProperties();
    if (Object.keys(clusterProperties).length > 0) {
      spec.clusterProperties = clusterProperties;
    }

    return spec;
  }

  private addClusteredSource(): void {
    const map = this.scene.getMapboxMap();
    if (map.getSource(this.clusteredSourceId)) {
      map.removeSource(this.clusteredSourceId);
    }
    map.addSource(this.clusteredSourceId, this.clusteredSourceSpec());
  }

  private addClusteredSourceLayers(): void {
    const map = this.scene.getMapboxMap();

    map.addLayer({
      id: this.mapboxClusteredPointCircleLayerId,
      type: "circle",
      source: this.clusteredSourceId,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": this.magnitudeCircleRadiusExpression(this.getScale()),
        "circle-color": this.pointColorExpression(),
        "circle-opacity": this.props.opacity ?? 1,
        "circle-stroke-color": "#FFFFFF",
        "circle-stroke-width": 2,
      },
      layout: { visibility: "none" },
    });

    map.addLayer({
      id: this.mapboxClusteredPointIconLayerId,
      type: "symbol",
      source: this.clusteredSourceId,
      filter: ["!", ["has", "point_count"]],
      paint: { "icon-opacity": this.props.opacity ?? 1 },
      layout: {
        visibility: "none",
        "icon-image": this.pointIconExpression(),
        "icon-size": this.magnitudeIconSizeExpression(this.getScale()),
        "icon-allow-overlap": true,
      },
    });

    map.addLayer(
      {
        id: this.mapboxClusterCircleLayerId,
        type: "circle",
        source: this.clusteredSourceId,
        filter: ["has", "point_count"],
        paint: {
          "circle-radius": this.clusterCircleRadiusExpression(this.getScale()),
          "circle-color": this.getDefaultColor(),
          "circle-opacity": this.props.opacity ?? 1,
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-opacity": this.props.opacity ?? 1,
          "circle-stroke-width": 2,
        },
        layout: { visibility: "none" },
      },
      this.mapboxIconLayerId,
    );

    map.addLayer({
      id: this.mapboxClusterCountLayerId,
      type: "symbol",
      source: this.clusteredSourceId,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-size": this.clusterTextSizeExpression(this.getScale()),
        "text-allow-overlap": true,
        visibility: "none",
      },
      paint: { "text-color": "#000" },
    });
  }

  private removeClusteredSourceLayers(): void {
    const map = this.scene.getMapboxMap();

    for (const id of [
      this.mapboxClusterCountLayerId,
      this.mapboxClusterCircleLayerId,
      this.mapboxClusteredPointIconLayerId,
      this.mapboxClusteredPointCircleLayerId,
    ]) {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    }
  }

  private rebuildClusteredSource(): void {
    const map = this.scene.getMapboxMap();
    this.clearClusterPieMarkers();

    const latest = this.getLatestSourceData().synchronous;

    this.removeClusteredSourceLayers();
    if (map.getSource(this.clusteredSourceId)) {
      map.removeSource(this.clusteredSourceId);
    }

    this.addClusteredSource();
    this.addClusteredSourceLayers();

    if (
      latest !== SynchrounousState.NotRequested &&
      latest !== SynchrounousState.StillPending &&
      latest !== SynchrounousState.PermanentlyFailed
    ) {
      const clustered = map.getSource(this.clusteredSourceId) as mapboxgl.GeoJSONSource;
      clustered?.setData(latest as any);
    }
  }

  private setLayerVisibility(layerId: string, visibility: "visible" | "none"): void {
    const map = this.scene.getMapboxMap();
    if (!map.getLayer(layerId)) {
      return;
    }
    map.setLayoutProperty(layerId, "visibility", visibility);
  }

  private applyLayerVisibility(show: boolean): void {
    const visOn = show ? "visible" : "none";
    const clusteringOn = this.isClusteringOn();
    const showPieClusters = clusteringOn && this.hasThresholds() && show;

    // Point rendering uses a composite icon, so keep circle helper layers hidden.
    this.setLayerVisibility(this.mapboxCircleLayerId, "none");
    this.setLayerVisibility(this.mapboxIconLayerId, clusteringOn ? "none" : visOn);

    this.setLayerVisibility(this.mapboxClusteredPointCircleLayerId, "none");
    this.setLayerVisibility(this.mapboxClusteredPointIconLayerId, clusteringOn ? visOn : "none");

    this.setLayerVisibility(this.mapboxClusterCircleLayerId, clusteringOn ? visOn : "none");
    this.setLayerVisibility(this.mapboxClusterCountLayerId, clusteringOn ? visOn : "none");
    this.updateClusterAggregateLayerOpacity(showPieClusters);

    if (showPieClusters) {
      this.syncClusterPieMarkers();
    } else {
      this.clearClusterPieMarkers();
    }
  }

  private updateClusterAggregateLayerOpacity(showPieClusters: boolean): void {
    const map = this.scene.getMapboxMap();
    const opacity = showPieClusters ? 0 : this.props.opacity ?? 1;

    if (map.getLayer(this.mapboxClusterCircleLayerId)) {
      map.setPaintProperty(this.mapboxClusterCircleLayerId, "circle-opacity", opacity);
      map.setPaintProperty(this.mapboxClusterCircleLayerId, "circle-stroke-opacity", opacity);
    }
    if (map.getLayer(this.mapboxClusterCountLayerId)) {
      map.setPaintProperty(this.mapboxClusterCountLayerId, "text-opacity", opacity);
    }
  }

  private retrieveEarthquakeEndpoint(
    request: GenericGeoJsonRequest,
    owner?: string,
  ): AsyncResult<GeoJSONFeatureCollection> {
    return networkCaches.geojson_cache.retrieveGeoJsonEndpoint(request, {
      kind: this.props.kind,
      format: this.normalizeEarthquakeFeatureCollection,
      owner,
    });
  }

  updateTrigger(): void {
    const t = roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName);
    const request = this.createRequest(t);
    const result = this.retrieveEarthquakeEndpoint(request, this.getGeoJsonRequestOwner());
    this.cancelSupersededGeoJsonRequests(result);

    this.updateSource(result, t, (data) => {
      const clustered = this.scene.getMapboxMap().getSource(this.clusteredSourceId) as mapboxgl.GeoJSONSource;
      clustered?.setData(data as any);
      this.syncClusterPieMarkers();
    });
  }

  beforeRender(): void {
    if (
      this.displayTimeChanged.changed(
        roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toMillis(),
      )
    ) {
      this.popup.remove();
    }

    if (this.scene.isAnimation()) {
      this.updateTrigger();
    } else {
      this.debouncedUpdateTrigger();
    }
  }

  private setEventListeners() {
    const map = this.scene.getMapboxMap();

    map.on("mouseenter", this.getHoverLayerIds(), this.onMouseEnter);
    map.on("mouseleave", this.getHoverLayerIds(), this.onMouseLeave);
    map.on("click", this.getPointLayerIds(), this.onPointLayerClick);
    map.on("click", this.getClusterLayerIds(), this.onClusterLayerClick);

    map.on("sourcedata", this.onSourceData);
    map.on("moveend", this.onViewportChanged);
    map.on("zoomend", this.onViewportChanged);
  }

  private renderEarthquakePopup(props: Record<string, unknown>): string {
    const row = (k: string, v: unknown) =>
      v == null || String(v) === "" ? null : ([k, String(v)] as [string, unknown]);

    const magnitude = formatMagnitude(props.magnitude);
    const magnitudeType = props.magnitude_type == null ? null : String(props.magnitude_type);
    const depth_km = formatDepthKm(props.depth_km);
    const issue_time = props.issue_time == null ? null : String(props.issue_time);
    const sourceLabel = mapSourceLabel(props.source);

    const rows = [
      row("Magnitude", magnitude),
      row("Type", magnitudeType),
      row("Depth (km)", depth_km),
      row("Issued", issue_time),
      row("Source", sourceLabel),
    ].filter(Boolean) as Array<[string, unknown]>;

    return renderPopup({ title: "Earthquake", rows });
  }

  peekLayerCache(request: GenericGeoJsonRequest): AsyncResult<GeoJSONFeatureCollection> {
    const cacheId = generateGenericCacheId(request, this.props.kind);
    return networkCaches.geojson_cache.peekCache(cacheId);
  }

  private expandCluster(clusterId: number, center: [number, number]): void {
    const source = this.scene.getMapboxMap().getSource(this.clusteredSourceId) as mapboxgl.GeoJSONSource;
    if (!source) {
      return;
    }

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom == null) {
        return;
      }
      this.scene.getMapboxMap().easeTo({ center, zoom });
    });
  }

  private computeClusterSlices(
    props: Record<string, unknown>,
    pointCount: number,
  ): Array<{ value: number; color: string }> {
    const thresholdSlices = this.getThresholds()
      .map((threshold, idx) => ({
        value: Number(props[this.thresholdClusterPropertyName(idx)] ?? 0),
        color: toSemiTransparentFillColor(threshold.color),
      }))
      .filter((slice) => Number.isFinite(slice.value) && slice.value > 0);

    const thresholdTotal = thresholdSlices.reduce((sum, slice) => sum + slice.value, 0);
    const defaultFromCluster = Number(props[this.defaultClusterPropertyName()]);
    const defaultValue = Number.isFinite(defaultFromCluster)
      ? defaultFromCluster
      : Math.max(0, pointCount - thresholdTotal);

    const slices = [...thresholdSlices];
    if (defaultValue > 0) {
      slices.push({ value: defaultValue, color: toSemiTransparentFillColor(this.getDefaultColor()) });
    }

    if (!slices.length) {
      return [{ value: pointCount, color: toSemiTransparentFillColor(this.getDefaultColor()) }];
    }

    return slices;
  }

  private buildClusterPieMarker(
    pointCount: number,
    totalMagnitude: number | undefined,
    slices: Array<{ value: number; color: string }>,
  ): HTMLElement {
    const sizeRadius = clusterRadiusFromMagnitude(pointCount, totalMagnitude) * this.getScale();
    const size = Math.max(16, sizeRadius * 2);
    const center = size / 2;
    const outerRadius = size / 2;
    const borderWidth = borderWidthFromOuterRadius(outerRadius);
    const pieRadius = Math.max(0, outerRadius - borderWidth / 2);
    const labelFontSize = Math.max(8, Math.round(size * 0.3));

    let currentAngle = -Math.PI / 2;
    const pathNodes: string[] = [];

    if (slices.length === 1) {
      pathNodes.push(`<circle cx="${center}" cy="${center}" r="${pieRadius}" fill="${slices[0].color}" />`);
    } else {
      const total = slices.reduce((sum, slice) => sum + slice.value, 0);
      for (const slice of slices) {
        const ratio = total > 0 ? slice.value / total : 0;
        const span = ratio * Math.PI * 2;
        const startAngle = currentAngle;
        const endAngle = currentAngle + span;

        pathNodes.push(
          `<path d="${pieSlicePath(center, center, pieRadius, startAngle, endAngle)}" fill="${slice.color}" />`,
        );

        currentAngle = endAngle;
      }
    }

    const marker = document.createElement("div");
    marker.style.width = `${size}px`;
    marker.style.height = `${size}px`;
    marker.style.cursor = "pointer";
    marker.style.opacity = String(this.props.opacity ?? 1);
    marker.innerHTML = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">${pathNodes.join(
      "",
    )}<circle cx="${center}" cy="${center}" r="${pieRadius}" fill="none" stroke="#ffffff" stroke-width="${borderWidth}"/><text x="${center}" y="${
      center + 1
    }" fill="#ffffff" stroke="rgba(0,0,0,0.45)" stroke-width="2" paint-order="stroke" font-size="${labelFontSize}" text-anchor="middle" dominant-baseline="middle" font-weight="700">${pointCount}</text></svg>`;

    return marker;
  }

  private clearClusterPieMarkers(): void {
    for (const marker of this.clusterPieMarkers.values()) {
      marker.remove();
    }
    this.clusterPieMarkers.clear();
  }

  private syncClusterPieMarkers(): void {
    if (!this.shouldRenderPieClusters()) {
      this.clearClusterPieMarkers();
      return;
    }

    const map = this.scene.getMapboxMap();
    if (!map.getSource(this.clusteredSourceId)) {
      this.clearClusterPieMarkers();
      return;
    }

    let features: mapboxgl.MapboxGeoJSONFeature[] = [];
    try {
      features = map.queryRenderedFeatures({
        layers: [this.mapboxClusterCircleLayerId],
      }) as mapboxgl.MapboxGeoJSONFeature[];
    } catch (_error) {
      features = [];
    }

    if (!features.length) {
      try {
        features = map.querySourceFeatures(this.clusteredSourceId) as mapboxgl.MapboxGeoJSONFeature[];
      } catch (_error) {
        return;
      }
    }

    const uniqueClusters = new Map<number, mapboxgl.MapboxGeoJSONFeature>();
    for (const feature of features) {
      const pointCount = Number(feature.properties?.point_count);
      if (!Number.isFinite(pointCount)) {
        continue;
      }
      const clusterId = Number(feature.properties?.cluster_id);
      if (!Number.isFinite(clusterId)) {
        continue;
      }
      if (!uniqueClusters.has(clusterId)) {
        uniqueClusters.set(clusterId, feature);
      }
    }

    this.clearClusterPieMarkers();

    for (const [clusterId, feature] of uniqueClusters) {
      if (feature.geometry.type !== "Point") {
        continue;
      }

      const coords = feature.geometry.coordinates as [number, number];
      const pointCount = Number(feature.properties?.point_count ?? 0);
      if (!Number.isFinite(pointCount) || pointCount <= 0) {
        continue;
      }

      const totalMagnitude = Number(feature.properties?.[this.clusterMagnitudeSumPropertyName()] ?? Number.NaN);
      const slices = this.computeClusterSlices((feature.properties || {}) as Record<string, unknown>, pointCount);
      const markerEl = this.buildClusterPieMarker(pointCount, totalMagnitude, slices);
      markerEl.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.expandCluster(clusterId, coords);
      });

      const marker = new mapboxgl.Marker({ element: markerEl, anchor: "center" }).setLngLat(coords).addTo(map);
      this.clusterPieMarkers.set(clusterId, marker);
    }
  }

  updateLayoutProperties(props?: SymbolLayout) {
    type SymbolLayoutProps = NonNullable<SymbolLayout>;
    const entries = Object.entries(props ?? {});
    for (const [k, v] of entries) {
      const name = k as keyof mapboxgl.LayoutSpecification;
      const value = v as mapboxgl.LayoutSpecification[typeof name];
      this.scene.getMapboxMap().setLayoutProperty(this.mapboxIconLayerId, name, value);

      if (k === "visibility") {
        const visibility = v as SymbolLayoutProps["visibility"];
        this.applyLayerVisibility(visibility !== "none");
      }
    }
  }

  checker(): PropsChecker<EarthquakeLayer, LayerBase<EarthquakeLayer>> {
    type CheckerProps = CheckedProps<EarthquakeLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        this.setLayerProps({ [property]: curr[property] });
        switch (property) {
          case "calibrated":
            break;
          case "show":
            this.applyLayerVisibility(curr.show);
            break;
          case "opacity": {
            const opacity = curr.opacity ?? 1;
            const map = this.scene.getMapboxMap();

            map.setPaintProperty(this.mapboxCircleLayerId, "circle-opacity", opacity);
            map.setPaintProperty(this.mapboxCircleLayerId, "circle-stroke-opacity", opacity);
            map.setPaintProperty(this.mapboxIconLayerId, "icon-opacity", opacity);

            map.setPaintProperty(this.mapboxClusteredPointCircleLayerId, "circle-opacity", opacity);
            map.setPaintProperty(this.mapboxClusteredPointCircleLayerId, "circle-stroke-opacity", opacity);
            map.setPaintProperty(this.mapboxClusteredPointIconLayerId, "icon-opacity", opacity);

            this.updateClusterAggregateLayerOpacity(this.shouldRenderPieClusters());

            this.syncClusterPieMarkers();
            break;
          }
          case "element_color": {
            const map = this.scene.getMapboxMap();
            this.updatePointColorPaint();
            map.setPaintProperty(this.mapboxClusterCircleLayerId, "circle-color", this.getDefaultColor());
            this.syncClusterPieMarkers();
            break;
          }
          case "thresholds": {
            this.rebuildClusteredSource();
            this.updatePointColorPaint();
            this.applyLayerVisibility(curr.show);
            break;
          }
          case "custom_options": {
            const map = this.scene.getMapboxMap();
            const clusteringOn = !!(
              curr.custom_options?.clustering ?? earthquakeLayerConfig.defaultEarthquakeLayerClustering
            );
            const prevClustering = !!(
              prev.custom_options?.clustering ?? earthquakeLayerConfig.defaultEarthquakeLayerClustering
            );

            if (curr.custom_options.icon_size !== prev.custom_options.icon_size) {
              const scale = this.getScale();

              map.setLayoutProperty(this.mapboxIconLayerId, "icon-size", this.magnitudeIconSizeExpression(scale));
              map.setPaintProperty(
                this.mapboxCircleLayerId,
                "circle-radius",
                this.magnitudeCircleRadiusExpression(scale),
              );

              map.setPaintProperty(
                this.mapboxClusterCircleLayerId,
                "circle-radius",
                this.clusterCircleRadiusExpression(scale),
              );
              map.setLayoutProperty(this.mapboxClusterCountLayerId, "text-size", this.clusterTextSizeExpression(scale));

              map.setLayoutProperty(
                this.mapboxClusteredPointIconLayerId,
                "icon-size",
                this.magnitudeIconSizeExpression(scale),
              );
              map.setPaintProperty(
                this.mapboxClusteredPointCircleLayerId,
                "circle-radius",
                this.magnitudeCircleRadiusExpression(scale),
              );

              this.syncClusterPieMarkers();
            }

            if (clusteringOn !== prevClustering) {
              this.applyLayerVisibility(curr.show);
            }

            break;
          }
          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }
      return changed;
    };

    return {
      opacity: checkLayerProp("opacity"),
      calibrated: checkLayerProp("calibrated"),
      thresholds: checkLayerProp("thresholds"),
      show: checkLayerProp("show"),
      element_color: checkLayerProp("element_color"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  createRequest(dateTimeWithOffset: DateTime): GenericGeoJsonRequest {
    const area = new Area(CoordinateSystem.WGS84, {
      east: 180,
      west: -180,
      north: 90,
      south: -90,
    });

    return {
      path: "get_earthquakes",
      area,
      boundingBoxLimit: getModelBoundingBox("mix"),
      query: {
        // get_earthquakes uses a start Datetime and duration.
        time_range: `${dateTimeWithOffset
          .minus(
            Duration.fromISO(
              this.props.custom_options?.time_duration ?? earthquakeLayerConfig.defaultEarthquakeLayerTimeDuration,
            ),
          )
          .toISO()}${
          this.props.custom_options?.time_duration ?? earthquakeLayerConfig.defaultEarthquakeLayerTimeDuration
        }`,
      },
    };
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const t = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(t);

    return asVoidAsyncResult(this.retrieveEarthquakeEndpoint(request));
  }

  getActiveWeatherParametersAsString(): { model: string; parameter: string }[] {
    return [];
  }

  moveZIndex(beforeLayerId: string) {
    const map = this.scene.getMapboxMap();
    super.moveZIndex(beforeLayerId);

    // 1) Cluster circle (background)
    map.moveLayer(this.mapboxClusterCircleLayerId, beforeLayerId);
    // 2) Raw point circles (visible when clustering is off)
    map.moveLayer(this.mapboxCircleLayerId, beforeLayerId);
    // 3) Unclustered point circles from clustered source (visible when clustering is on)
    map.moveLayer(this.mapboxClusteredPointCircleLayerId, beforeLayerId);
    // 4) Raw point icons
    map.moveLayer(this.mapboxIconLayerId, beforeLayerId);
    // 5) Unclustered point icons from clustered source
    map.moveLayer(this.mapboxClusteredPointIconLayerId, beforeLayerId);
    // 6) Cluster count labels (top)
    map.moveLayer(this.mapboxClusterCountLayerId, beforeLayerId);
  }

  removeLayer(): void {
    this.debouncedUpdateTrigger.cancel();

    const map = this.scene.getMapboxMap();

    this.popup.remove();
    this.clearClusterPieMarkers();

    map.off("mouseenter", this.getHoverLayerIds(), this.onMouseEnter);
    map.off("mouseleave", this.getHoverLayerIds(), this.onMouseLeave);
    map.off("click", this.getPointLayerIds(), this.onPointLayerClick);
    map.off("click", this.getClusterLayerIds(), this.onClusterLayerClick);
    map.off("sourcedata", this.onSourceData);
    map.off("moveend", this.onViewportChanged);
    map.off("zoomend", this.onViewportChanged);

    const anyMap = map as any;
    if (typeof anyMap?.hasImage === "function" && typeof anyMap?.removeImage === "function") {
      for (const id of this.pointCompositeIconIds) {
        if (anyMap.hasImage(id)) {
          anyMap.removeImage(id);
        }
      }
    }
    this.pointCompositeIconIds.clear();

    this.removeClusteredSourceLayers();

    if (map.getLayer(this.mapboxIconLayerId)) {
      map.removeLayer(this.mapboxIconLayerId);
    }
    if (map.getLayer(this.mapboxCircleLayerId)) {
      map.removeLayer(this.mapboxCircleLayerId);
    }

    if (map.getSource(this.clusteredSourceId)) {
      map.removeSource(this.clusteredSourceId);
    }

    super.removeLayer();
  }
}
